import { inputGradient, syncPaintInput } from "./native-paint-input";
import { NativeToolGroups } from "./native-tool-groups";
import { originalIcon } from "./native-original-icons";
import { blobDataURL as readAudio } from "../adapters/blob-data-url";
import {
  originalCompact,
  syncOriginalCompact,
} from "./native-original-controls";
import controls from "./native-interactive-controls.html?raw";
import "./native-interactive.css";
import type { NativeSession } from "../native/session";
import type { ImageStore } from "../render/image-store";
import { readImageFile } from "../render/image-store";
import {
  assertInteraction,
  createInteractive,
  isInteractive,
  roleNames,
  type InteractiveRole,
} from "../native/interactive";
import type { SceneNode } from "../scene/model";
const fields: Record<string, string> = {
  "question-id": "questionId",
  "display-text": "displayText",
  "stats-label": "statsLabel",
  "answer-group": "answerGroup",
  "stats-save-mode": "statsSaveMode",
  "correct-value": "correctValue",
  "target-id": "targetId",
  "target-screen-id": "targetScreenId",
  score: "scoreValue",
  "is-correct": "isCorrectOption",
  "hover-animation": "hoverAnimation",
  "click-animation": "clickAnimation",
  "release-animation": "releaseAnimation",
  "visibility-enabled": "visibilityConditionEnabled",
  "visibility-condition": "visibilityConditionType",
  "visibility-question-id": "visibilityQuestionId",
  "font-size": "fontSize",
  "font-family": "fontFamily",
  "text-color": "textColor",
};
const visible: Record<InteractiveRole, string[]> = {
  quizButton: [
    "display-text",
    "image",
    "target-screen-id",
    "visibility-condition",
    "sounds",
    "animations",
  ],
  inputField: [
    "question-id",
    "display-text",
    "statistics",
    "stats-label",
    "correct-value",
    "score",
    "stats-save-mode",
    "sounds",
    "animations",
  ],
  choiceOption: [
    "question-id",
    "display-text",
    "statistics",
    "stats-label",
    "answer-group",
    "image",
    "score",
    "is-correct",
    "stats-save-mode",
    "sounds",
    "animations",
  ],
  dropZone: ["display-text", "image", "target-id", "sounds", "animations"],
  dragItem: [
    "question-id",
    "display-text",
    "statistics",
    "stats-label",
    "image",
    "target-id",
    "score",
    "stats-save-mode",
    "sounds",
    "animations",
  ],
};
const el = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T;
export class NativeInteractive {
  private panel: HTMLElement;
  private dynamic = document.createElement("div");
  private previous: SceneNode | undefined;
  private uploading = 0;
  private compact: HTMLElement;
  private portal: {
    section: HTMLElement;
    placeholder: Comment;
    popup: HTMLElement;
  } | null = null;
  constructor(
    private session: NativeSession,
    private images: ImageStore,
    private prepare: () => boolean,
    private changed: () => void,
    private message: (s: string, error?: boolean) => void,
  ) {
    const container = document.createElement("div");
    container.innerHTML = controls;
    this.panel = container.firstElementChild as HTMLElement;
    document
      .querySelector("#native-right-panel .native-panel-scroll")!
      .append(this.panel);
    this.dynamic.id = "dynamic-text-section";
    this.dynamic.className = "interactive-row";
    this.dynamic.innerHTML =
      '<h3>Динамический текст</h3><select id="dynamic-text-type"><option value="none">Обычный текст</option><option value="studentName">ФИО ученика</option><option value="lastResult">Результат последнего задания</option><option value="totalResult">Общий результат</option></select>';
    this.panel.after(this.dynamic);
    this.panel.querySelector("h3")!.hidden = true;
    const groups = [...this.panel.querySelectorAll(":scope > details")];
    const main = document.createElement("details");
    main.id = "interactive-main-group";
    main.className = "native-properties property-group";
    main.innerHTML =
      '<summary><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h11V3l5 4-5 4V8H4V6zm16 12H9v3l-5-4 5-4v3h11v2z"/></svg><span class="property-group-title">Интерактивность</span><span class="property-group-chevron" aria-hidden="true"></span></summary><div class="native-property-body property-group-body"></div>';
    for (const child of [...this.panel.children])
      if (child.tagName !== "H3" && !groups.includes(child))
        main.lastElementChild!.append(child);
    this.panel.append(main);
    for (const g of groups) {
      g.classList.add("native-properties", "property-group");
      g.querySelector(".collapsible-section-body")?.classList.add(
        "native-property-body",
        "property-group-body",
      );
      this.panel.append(g);
    }
    const dimensions =
      document.querySelector('[data-property-section="dimensions"]') ||
      document.getElementById("native-dimensions");
    if (dimensions) dimensions.after(this.panel);

    el<HTMLSelectElement>("dynamic-text-type").onchange = () => {
      const n = session.selected;
      if (n?.kind === "text" && prepare()) {
        session.replaceNode({
          ...n,
          interaction: {
            ...n.interaction,
            dynamicTextType: el<HTMLSelectElement>("dynamic-text-type").value,
          },
        });
        session.finish();
        changed();
      }
    };
    const typography = document.createElement("details");
    typography.className =
      "native-properties interactive-row collapsible-section";
    typography.innerHTML =
      '<summary>Текст элемента</summary><div class="collapsible-section-body font-controls"><select class="font-select" id="interactive-font-family" aria-label="Шрифт"></select><input id="interactive-font-size" type="number" min="1" max="1000" aria-label="Размер шрифта"><input id="interactive-text-color" type="color" class="color-picker" aria-label="Цвет текста"></div>';
    el<HTMLSelectElement>("font-family")
      .querySelectorAll("option")
      .forEach((option) =>
        typography.querySelector("select")!.append(option.cloneNode(true)),
      );
    this.panel.append(typography);
    for (const [suffix, key] of Object.entries(fields)) {
      const input = el<HTMLInputElement | HTMLSelectElement>(
        "interactive-" + suffix,
      );
      const update = () => {
        const n = session.selected;
        if (!n || !isInteractive(n) || !prepare()) return;
        let value: unknown =
          input instanceof HTMLInputElement && input.type === "checkbox"
            ? input.checked
            : input.value;
        if (["scoreValue", "fontSize"].includes(key)) value = Number(value);
        if (["questionId", "targetId", "correctValue"].includes(key))
          value = String(value).trim();
        const paint = input as HTMLInputElement;
        const patch =
          key === "textColor"
            ? paint.dataset.colorChannel === "gradient"
              ? { textGradient: inputGradient(paint) }
              : paint.dataset.colorChannel === "alpha"
                ? { textAlpha: Number(paint.dataset.colorAlpha ?? 1) }
                : {
                    textColor: value,
                    ...(n.interaction.textGradient
                      ? { textGradient: null }
                      : {}),
                  }
            : { [key]: value };
        const next: SceneNode = {
          ...n,
          interaction: { ...n.interaction, ...patch },
        };
        try {
          assertInteraction(next, true);
          session.replaceNode(next);
          session.finish();
          changed();
          if (key === "fontFamily")
            void document.fonts
              .load(`${next.interaction.fontSize}px ${JSON.stringify(value)}`)
              .then(changed)
              .catch(() => message("Не удалось загрузить шрифт.", true));
        } catch (e) {
          message((e as Error).message, true);
        }
      };
      input.onchange = update;
      if (input instanceof HTMLInputElement && input.type === "text")
        input.oninput = update;
    }
    const toolbar =
      document.querySelector("#native-toolbar .native-tool-buttons") ||
      document.getElementById("native-toolbar") ||
      document.querySelector(".native-toolbar");
    if (!toolbar) throw new Error("Панель инструментов не найдена.");
    const group = document.createElement("div");
    group.className = "dropdown tool-group";
    group.dataset.toolGroupContainer = "interactive";
    group.innerHTML =
      '<button type="button" class="bottom-tool-button tool-group-trigger" data-tool-group-trigger="interactive" title="Кнопка" aria-label="Интерактивные инструменты"><svg class="selected-tool-icon" viewBox="0 0 24 24"><use href="#tool-icon-button"/></svg><svg class="tool-chevron" viewBox="0 0 12 12"><path d="m3 7 3-3 3 3z"/></svg></button><div class="dropdown-content" role="menu" hidden></div>';
    const roleMap: Record<string, InteractiveRole> = {};
    for (const [role, id, icon] of [
      ["quizButton", "add-quiz-button", "button"],
      ["inputField", "add-input-field", "input"],
      ["choiceOption", "add-choice-option", "choice"],
      ["dropZone", "add-drop-zone", "drop-zone"],
      ["dragItem", "add-drag-item", "drag"],
    ] as const) {
      const b = document.createElement("button");
      b.id = id;
      b.type = "button";
      b.dataset.toolGroup = "interactive";
      b.dataset.toolLabel = roleNames[role];
      b.dataset.toolIcon = "tool-icon-" + icon;
      b.innerHTML = `<svg viewBox="0 0 24 24"><use href="#tool-icon-${icon}"/></svg><span>${roleNames[role]}</span>`;
      group.lastElementChild!.append(b);
      roleMap[id] = role;
    }
    toolbar.append(group);
    new NativeToolGroups<InteractiveRole>(
      (role) => {
        if (!prepare()) return;
        const n = createInteractive(
          crypto.randomUUID(),
          role,
          session.page.size.width / 2,
          session.page.size.height / 2,
        );
        session.replaceNodes([...session.page.nodes, n]);
        session.select([n.id]);
        session.finish();
        changed();
      },
      [group],
      roleMap,
    );
    el("interactive-image-upload-btn").onclick = () =>
      el("interactive-image-upload").click();
    el<HTMLInputElement>("interactive-image-upload").onchange = () => {
      const input = el<HTMLInputElement>("interactive-image-upload"),
        file = input.files?.[0];
      input.value = "";
      if (file) void this.upload(file, "interactiveImage");
    };
    el("interactive-image-remove-btn").onclick = () =>
      this.remove("interactiveImage");
    let slot: "hoverSound" | "clickSound" | "releaseSound" = "hoverSound";
    for (const b of this.panel.querySelectorAll<HTMLButtonElement>(
      ".interactive-sound-btn",
    ))
      b.onclick = () => {
        slot = (b.dataset.soundSlot + "Sound") as typeof slot;
        el("interactive-sound-upload").click();
      };
    for (const b of this.panel.querySelectorAll<HTMLButtonElement>(
      ".interactive-sound-remove-btn",
    ))
      b.onclick = () =>
        this.remove((b.dataset.soundSlot + "Sound") as typeof slot);
    el<HTMLInputElement>("interactive-sound-upload").onchange = () => {
      const input = el<HTMLInputElement>("interactive-sound-upload"),
        file = input.files?.[0];
      input.value = "";
      if (file) void this.upload(file, slot);
    };
    this.compact = originalCompact("native-interactive-compact", [
      {
        id: "interactive-image-upload-btn",
        icon: "plus-image",
        title: "Добавить изображение",
      },
      {
        id: "interactive-image-remove-btn",
        icon: "delete",
        title: "Удалить изображение",
      },
      { id: "interactive-question-id", icon: "id", title: "ID вопроса" },
      {
        id: "interactive-display-text",
        icon: "text",
        title: "Отображаемый текст",
      },
      {
        id: "interactive-correct-value",
        icon: "correct",
        title: "Правильное значение",
      },
      {
        id: "interactive-target-id",
        icon: "target",
        title: "Целевая зона / связанный ID",
      },
      {
        id: "interactive-target-screen-id",
        icon: "screen",
        title: "Переход на экран",
      },
      { id: "interactive-score", icon: "score", title: "Баллы" },
      {
        id: "interactive-is-correct",
        icon: "correct",
        title: "Правильный вариант",
      },
      {
        id: "interactive-visibility-enabled",
        icon: "visible",
        title: "Показывать по условию",
      },
      {
        id: "interactive-hover-animation",
        icon: "motion",
        title: "Анимация при наведении",
      },
      {
        id: "interactive-click-animation",
        icon: "motion",
        title: "Анимация при клике",
      },
      {
        id: "interactive-release-animation",
        icon: "motion",
        title: "Анимация при отпускании",
      },
      { id: "interactive-font-family", icon: "font", title: "Шрифт" },
      {
        id: "interactive-font-size",
        icon: "font-size",
        title: "Размер шрифта",
      },
      { id: "interactive-text-color", icon: "color", title: "Цвет текста" },
    ]);
    document.getElementById("native-compact-properties")!.after(this.compact);
    // Compact popups reuse the original rows and their command handlers.
    for (const [section, icon, title] of [
      [main, "motion", "Интерактивность"],
      ...groups.map((g) => [
        g,
        "motion",
        g.querySelector("summary")!.textContent!.trim(),
      ]),
    ] as [HTMLElement, string, string][]) {
      const anchor = document.createElement("div");
      anchor.className = "native-property-anchor";
      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.title = trigger.ariaLabel = title;
      trigger.dataset.interactiveSection = section.id;
      trigger.innerHTML = originalIcon(icon);
      trigger.onclick = () => {
        const same = this.portal?.section === section;
        this.closePortal();
        if (same) return;
        this.compact
          .querySelectorAll<HTMLElement>(".native-property-popup")
          .forEach((p) => (p.hidden = true));
        const placeholder = document.createComment(
            "interactive property position",
          ),
          popup = document.createElement("div");
        popup.className = "native-property-popup native-interactive-popup";
        section.before(placeholder);
        popup.append(section);
        anchor.append(popup);
        (section as HTMLDetailsElement).open = true;
        this.portal = { section, placeholder, popup };
      };
      anchor.append(trigger);
      this.compact.append(anchor);
    }
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!this.portal?.popup.parentElement?.contains(e.target as Node))
          this.closePortal();
      },
      true,
    );
    this.compact.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.portal) {
        e.stopPropagation();
        this.closePortal();
      }
    });
    window.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || document.querySelector("dialog[open]")) return;
      const popups = this.compact.querySelectorAll<HTMLElement>(
        ".native-property-popup:not([hidden])",
      );
      if (!this.portal && !popups.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.closePortal();
      popups.forEach((p) => (p.hidden = true));
    });
  }
  private closePortal() {
    if (!this.portal) return;
    const { placeholder, section, popup } = this.portal;
    this.portal = null;
    placeholder.replaceWith(section);
    popup.remove();
  }
  private remove(key: keyof SceneNode["media"]) {
    const n = this.session.selected;
    if (!n || !isInteractive(n) || !this.prepare()) return;
    const media = { ...n.media };
    delete media[key];
    this.session.replaceNode({
      ...n,
      media,
      interaction: {
        ...n.interaction,
        ...(key === "interactiveImage"
          ? { interactiveImageEnabled: false, imageName: "" }
          : { [key + "Name"]: "" }),
      },
    });
    this.session.finish();
    this.changed();
  }
  private async upload(file: File, key: keyof SceneNode["media"]) {
    const n = this.session.selected;
    if (!n || !isInteractive(n) || !this.prepare()) return;
    const token = ++this.uploading,
      rev = this.session.revision,
      gen = this.session.generation;
    try {
      const audio = key !== "interactiveImage";
      if (
        audio &&
        (!/^audio\/(mpeg|ogg|wav|x-wav|mp4|aac)$/i.test(file.type) ||
          file.size > 10 * 1024 * 1024)
      )
        throw new Error("Выберите MP3, OGG, WAV, M4A или AAC до 10 МБ.");
      const source = audio ? await readAudio(file) : await readImageFile(file),
        asset = {
          id: crypto.randomUUID(),
          kind: audio ? ("audio" as const) : ("image" as const),
          source,
        };
      await this.images.prepare([asset]);
      if (
        token !== this.uploading ||
        rev !== this.session.revision ||
        gen !== this.session.generation ||
        n.id !== this.session.selectedId ||
        !this.prepare()
      )
        throw new Error("Загрузка отменена: вы начали другое действие.");
      this.session.document = {
        ...this.session.document,
        assets: [...this.session.document.assets, asset],
      };
      this.session.replaceNode({
        ...n,
        media: { ...n.media, [key]: asset.id },
        interaction: {
          ...n.interaction,
          ...(audio
            ? { [key + "Name"]: file.name }
            : { interactiveImageEnabled: true, imageName: file.name }),
        },
      });
      this.session.finish();
      this.changed();
    } catch (e) {
      this.message((e as Error).message, true);
    }
  }
  render() {
    const n = this.session.selected,
      role = n?.interaction.role as InteractiveRole;
    if (this.previous?.id !== n?.id) this.closePortal();
    this.panel.hidden = !n || !isInteractive(n);
    this.compact.hidden = this.panel.hidden;
    if (this.previous?.id !== n?.id)
      for (const popup of this.compact.querySelectorAll<HTMLElement>(
        ".native-property-popup",
      ))
        popup.hidden = true;
    for (const trigger of this.compact.querySelectorAll<HTMLElement>(
      "[data-original-control]",
    )) {
      const source = el<HTMLElement>(trigger.dataset.originalControl!);
      const row = source.closest<HTMLElement>('[id^="row-interactive-"]');
      if (row)
        trigger.parentElement!.hidden =
          this.panel.hidden ||
          !visible[role].includes(row.id.replace("row-interactive-", ""));
    }
    this.dynamic.hidden = n?.kind !== "text";
    if (
      n?.kind === "text" &&
      document.activeElement !== el("dynamic-text-type")
    )
      el<HTMLSelectElement>("dynamic-text-type").value = String(
        n.interaction.dynamicTextType || "none",
      );
    if (!n || this.panel.hidden) {
      this.previous = n;
      return;
    }
    el("native-selection-title").textContent = roleNames[role];
    if (this.previous === n) return;
    this.previous = n;
    el("interactive-role-text").textContent = roleNames[role];
    el("interactive-name-text").textContent = String(n.layer.name || "");
    for (const row of document.querySelectorAll<HTMLElement>(
      '[id^="row-interactive-"]',
    ))
      row.hidden = !visible[role].includes(
        row.id.replace("row-interactive-", ""),
      );
    for (const trigger of this.compact.querySelectorAll<HTMLElement>(
      "[data-interactive-section]",
    ))
      trigger.parentElement!.hidden = el(
        trigger.dataset.interactiveSection!,
      ).hidden;
    const options = (
      id: string,
      entries: { value: string; name: string }[],
    ) => {
      const field = el<HTMLSelectElement>(id),
        value = field.value;
      field.replaceChildren(
        ...[{ value: "", name: "Не выбрано" }, ...entries].map(
          (v) => new Option(v.name, v.value),
        ),
      );
      field.value = value;
    };
    options(
      "interactive-target-screen-id",
      this.session.document.pages.map((p) => ({ value: p.id, name: p.name })),
    );
    options(
      "interactive-visibility-question-id",
      [
        ...new Set(
          this.session.page.nodes
            .map((q) => String(q.interaction.questionId || ""))
            .filter(Boolean),
        ),
      ].map((q) => ({ value: q, name: q })),
    );
    for (const [suffix, key] of Object.entries(fields)) {
      const input = el<HTMLInputElement | HTMLSelectElement>(
        "interactive-" + suffix,
      );
      if (input instanceof HTMLInputElement && input.type === "checkbox")
        input.checked = n.interaction[key] === true;
      else if (document.activeElement !== input)
        input.value = String(n.interaction[key] ?? "");
    }
    syncPaintInput(
      el<HTMLInputElement>("interactive-text-color"),
      n.interaction.textGradient,
    );
    el<HTMLInputElement>("interactive-text-color").dataset.displayAlpha =
      String(n.interaction.textAlpha ?? 1);
    el("interactive-target-id-label").textContent =
      role === "dropZone" ? "ID зоны ответа" : "Целевая зона";
    el("interactive-visibility-settings").hidden =
      !n.interaction.visibilityConditionEnabled;
    el("interactive-visibility-question-row").hidden = String(
      n.interaction.visibilityConditionType,
    ).startsWith("all");
    el("interactive-image-name").textContent = String(
      n.interaction.imageName || "Изображение не выбрано",
    );
    for (const slot of ["hover", "click", "release"])
      el("interactive-" + slot + "-sound-name").textContent = String(
        n.interaction[slot + "SoundName"] || "Звук не выбран",
      );
    syncOriginalCompact("native-interactive-compact");
  }
}
